import {
    Component,
    inject,
    type OnDestroy,
    type OnInit,
    signal
} from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { NavigationComponent } from './components/navigation/navigation.component';

import { filter, Subject, takeUntil } from 'rxjs';

import { Title } from '@angular/platform-browser';
import { environment } from '../../environments/environment';
import { Toast } from 'primeng/toast';
import {
    MSAL_GUARD_CONFIG,
    MsalBroadcastService,
    type MsalGuardConfiguration,
    MsalService
} from '@azure/msal-angular';
import {
    type AccountInfo,
    type AuthenticationResult,
    InteractionStatus,
    type RedirectRequest
} from '@azure/msal-browser';
import { Badge } from 'primeng/badge';
import { embarkColors } from './colors';

@Component({
    selector: 'app-root',
	imports: [RouterOutlet, NavigationComponent, Toast, Badge],
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.sass']
})
export class AppComponent implements OnInit, OnDestroy {
    public isNavClosed = false;
    public loginDisplay = false;
    public isIframe = false;
    public isLoggedIn = signal<boolean>(false);
    protected readonly envLabel =
        environment.name !== 'production' ? environment.name : null;
    protected readonly embarkColors = embarkColors;

    private readonly titleService = inject<Title>(Title);

    private msalGuardConfig = inject<MsalGuardConfiguration>(MSAL_GUARD_CONFIG);
    private msalService = inject<MsalService>(MsalService);
    private msalBroadcastService =
        inject<MsalBroadcastService>(MsalBroadcastService);
    private _destroying$ = new Subject<void>();
    private activeAccount = signal<AccountInfo | null>(null);

    public signIn(): void {
        const authRequest = this.msalGuardConfig.authRequest
            ? { ...(this.msalGuardConfig.authRequest as RedirectRequest) }
            : undefined;
        this.msalService.loginRedirect(authRequest);
    }

    private updateLoginState(): void {
        let active: AccountInfo | null =
            this.msalService.instance.getActiveAccount();

        if (!active && this.msalService.instance.getAllAccounts().length > 0) {
            active = this.msalService.instance.getAllAccounts()[0] ?? null;
            this.msalService.instance.setActiveAccount(active);
        }

        this.activeAccount.set(active);
        this.isLoggedIn.set(!!active);
    }

    public ngOnInit(): void {
        let siteTitle = 'Integration Portal';
        if (environment.name == 'local' || environment.name == 'development') {
            siteTitle = siteTitle + ' - ' + environment.name;
        }
        this.titleService.setTitle(siteTitle);

        this.msalService.handleRedirectObservable().subscribe({
            next: (result: AuthenticationResult | null) => {
                if (result?.account) {
                    this.msalService.instance.setActiveAccount(result.account);
                    this.updateLoginState();
                } else {
                    // Check cache before triggering a new login
                    const account =
                        this.msalService.instance.getActiveAccount() ??
                        this.msalService.instance.getAllAccounts()[0] ??
                        null;

                    if (account) {
                        this.msalService.instance.setActiveAccount(account);
                        this.updateLoginState();
                    } else {
                        this.signIn(); // only redirect if truly not logged in
                    }
                }
            },
            error: function (err) {
                console.error('Redirect error:', err);
            }
        });

        this.msalBroadcastService.inProgress$
            .pipe(
                filter(
                    (status: InteractionStatus) =>
                        status === InteractionStatus.None
                ),
                takeUntil(this._destroying$)
            )
            .subscribe(() => {
                this.updateLoginState();
            });
    }

    public handleToggleNavigation($event: boolean): void {
        this.isNavClosed = !$event;
    }

    //triggers destroying observable
    public ngOnDestroy(): void {
        this._destroying$.next(undefined);
        this._destroying$.complete();
    }}
